import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import { SearchInput } from "app/components/atoms/input";
import Typography from "app/components/atoms/typography";
import { ModalMolecule } from "app/components/molecules/modal";
import api from "app/integration/api";
import { useEffect, useRef, useState } from "react";
import { XMarkIcon, QrCodeIcon, CameraIcon } from "@heroicons/react/24/outline";
import { Html5QrcodeScanner } from "html5-qrcode";
import "app/styles/qrscanner.css";

function FindProductCode({ onSuccess = () => {} }) {
  const searchInputRef = useRef(null);
  const { showStates, closeModal } = useApplicationModals();

  const handleClose = () => {
    closeModal("productCode.find");
  };

  const focusInput = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  };


  const searchTimer = useRef(null);
  const qrScannerRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [productCode, setProductCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useCameraScanner, setUseCameraScanner] = useState(false);

  const searchProductCode = async (qrCode) => {
    try {
      setLoading(true);
      const response = await api.productCode.index({
        qr_code: qrCode,
        limit: 1,
      });

      if (response.data && response.data.length > 0) {
        setProductCode(response.data[0]);
      } else {
        setProductCode(null);
      }
    } catch (error) {
      console.error("Error searching product code:", error);
      setProductCode(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showStates["productCode.find"]) {
      // Focus immediately and after a short delay to ensure focus
      if (!useCameraScanner) {
        focusInput();
        const focusTimer = setTimeout(focusInput, 100);
        return () => clearTimeout(focusTimer);
      }
    }
  }, [showStates, useCameraScanner]);

  useEffect(() => {
    // Initialize QR scanner when camera mode is enabled
    if (useCameraScanner && showStates["productCode.find"]) {
      // Clean up any existing scanner
      if (qrScannerRef.current) {
        qrScannerRef.current.clear();
      }

      // Create new scanner
      const qrScanner = new Html5QrcodeScanner(
        "html5-qr-code-scanner",
        { fps: 10, qrbox: { width: 250, height: 250 } },
        /* verbose= */ false
      );

      qrScanner.render((decodedText) => {
        // Success callback
        // Clean up the scanned text - remove %22 (URL-encoded double quotes) and actual quotes
        const cleanedText = decodedText.replace(/%22/g, "").replace(/"/g, "");
        console.log(`QR Code detected: ${cleanedText}`);
        setSearchInput(cleanedText);
        searchProductCode(cleanedText);
        
        // Optionally pause scanning after successful detection
        if (qrScannerRef.current) {
          qrScannerRef.current.pause();
          // Resume after a delay
          setTimeout(() => {
            if (qrScannerRef.current) {
              qrScannerRef.current.resume();
            }
          }, 3000);
        }
      }, (errorMessage) => {
        // Error callback
        console.error(`QR Code scanning error: ${errorMessage}`);
      });

      qrScannerRef.current = qrScanner;

      // Clean up on unmount
      return () => {
        if (qrScannerRef.current) {
          qrScannerRef.current.clear();
          qrScannerRef.current = null;
        }
      };
    }
  }, [useCameraScanner, showStates]);

  return (
    <ModalMolecule
      show={showStates["productCode.find"]}
      title="Find Product QR Code"
      onClose={handleClose}
      onEntered={focusInput}
    >
      <div className="space-y-4">
        {/* Toggle between camera and manual input */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button data-testid="modal-find-product-code-qr-code-button"
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                !useCameraScanner
                  ? "bg-primary-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              } border border-gray-300`}
              onClick={() => setUseCameraScanner(false)}
            >
              <QrCodeIcon className="h-5 w-5 inline mr-1" />
              Manual Input
            </button>
            <button data-testid="modal-find-product-code-camera-button"
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                useCameraScanner
                  ? "bg-primary-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              } border border-gray-300`}
              onClick={() => setUseCameraScanner(true)}
            >
              <CameraIcon className="h-5 w-5 inline mr-1" />
              Camera Scan
            </button>
          </div>
        </div>
        
        {!useCameraScanner ? (
          <div className="relative">
            <input data-testid="modal-find-product-code-scan-qr-code-input"
              className="w-full rounded-lg border border-gray-300 pr-10 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Scan QR Code"
              type="text"
              value={searchInput}
              ref={searchInputRef}
              autoFocus
              onFocus={(e) => {
                e.target.select();
              }}
              onClick={(e) => {
                e.target.select();
              }}
              onBlur={(e) => {
                // Immediately refocus if blur occurs
                if (searchInputRef.current && !useCameraScanner) {
                  searchInputRef.current.focus();
                  searchInputRef.current.select();
                }
              }}
              onChange={(e) => {
                const value = e.target.value?.replace(/"/g, "");
                setSearchInput(value);

                if (searchTimer.current) {
                  clearTimeout(searchTimer.current);
                }

                searchTimer.current = setTimeout(() => {
                  if (value.trim()) {
                    searchProductCode(value.trim());
                  }
                }, 3000);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (searchTimer.current) {
                    clearTimeout(searchTimer.current);
                  }
                  const value = e.target.value?.replace(/"/g, "");
                  if (value.trim()) {
                    searchProductCode(value.trim());
                  }
                }
              }}
            />
            {searchInput && (
              <button data-testid="modal-find-product-code-x-mark-button"
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchInput("");
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        ) : (
          <div>
            <div id="html5-qr-code-scanner" className="qr-scanner-container"></div>
            <div className="text-center text-sm text-gray-500 mt-2">
              Point your camera at a QR code to scan
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-4">
            <span className="text-gray-500">Searching...</span>
          </div>
        )}

        {!loading && productCode && (
          <div className="space-y-4">
            {/* Reference and QR Code */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500">Reference</div>
              <div className="text-lg pb-4">{productCode.ref || "-"}</div>
              <div className="text-sm text-gray-500">QR Code</div>
              <div className="font-mono text-lg">{productCode.qr_code}</div>
            </div>

            {/* Status */}
            <div>
              <div className="text-sm text-gray-500 mb-1">Status</div>
              <Badge
                size="xl"
                color={
                  productCode.status === "warehouse"
                    ? "gray"
                    : productCode.status === "packing"
                    ? "blue"
                    : productCode.status === "delivery"
                    ? "green"
                    : productCode.status === "return"
                    ? "yellow"
                    : productCode.status === "scrap"
                    ? "red"
                    : "gray"
                }
              >
                {productCode.status}
              </Badge>
            </div>

            {/* Timeline */}
            <div className="space-y-2 pt-4">
              <div className="text-sm text-gray-500">Timeline</div>
              <div className="space-y-2 text-lg">
                <div className="grid grid-cols-4">
                  <span>Received</span>
                  <span>
                    {productCode.createdAt ? (
                      <Typography.Date
                        format="dd/MM/yy hh:mm"
                        date={productCode.createdAt}
                      />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-4">
                  <span>Packed</span>
                  <span>
                    {productCode.scan_packing_date ? (
                      <Typography.Date
                        format="dd/MM/yy hh:mm"
                        date={productCode.scan_packing_date}
                      />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-4">
                  <span>Delivered</span>
                  <span>
                    {productCode.scan_delivery_date ? (
                      <Typography.Date
                        format="dd/MM/yy hh:mm"
                        date={productCode.scan_delivery_date}
                      />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-4">
                  <span>Returned</span>
                  <span>
                    {productCode.scan_retur_date ? (
                      <Typography.Date
                        format="dd/MM/yy hh:mm"
                        date={productCode.scan_retur_date}
                      />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-4">
                  <span>Scrapped</span>
                  <span>
                    {productCode.scan_scrap_date ? (
                      <Typography.Date
                        format="dd/MM/yy hh:mm"
                        date={productCode.scan_scrap_date}
                      />
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !productCode && searchInput && (
          <div className="text-center py-4">
            <span className="text-gray-500">No product code found</span>
          </div>
        )}
      </div>
    </ModalMolecule>
  );
}

export default FindProductCode;
